import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchCasaMapaAdminListingDataReports,
  patchCasaMapaAdminListingDataReport,
} from "../../../api/clientCasamapaAdminApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale } from "../../../intl";
import "../../../styles/casamapaAdminPortal.css";

const COPY = "realEstate.casaMapa.admin.listingDataReports";
const STATUS_FILTERS = ["", "open", "reviewing", "resolved", "dismissed"];
const TRIAGE_ACTIONS = ["reviewing", "resolved", "dismissed"];

function formatWhen(value, locale) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(locale || "en", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function StatusBadge({ status, t }) {
  return (
    <span className={`cm-data-reports__badge cm-data-reports__badge--${status || "open"}`}>
      {t(`${COPY}.statuses.${status}`, { defaultValue: status })}
    </span>
  );
}

export default function CasaMapaAdminListingDataReportsPage() {
  const { t, locale } = useLocale();
  const { clientViewId, path } = useCasaMapaAdminPortal();
  const [status, setStatus] = useState("open");
  const [reasonCode, setReasonCode] = useState("");
  const [reports, setReports] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!clientViewId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchCasaMapaAdminListingDataReports(clientViewId, {
        status: status || undefined,
        reasonCode: reasonCode || undefined,
        limit: 100,
      });
      setReports(data.reports || []);
      setReasons(data.reasons || []);
      setOpenCount(data.openCount || 0);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.message || t(`${COPY}.loadError`));
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, status, reasonCode, t]);

  useEffect(() => {
    load();
  }, [load]);

  const triage = async (reportId, nextStatus) => {
    setBusyId(reportId);
    try {
      await patchCasaMapaAdminListingDataReport(clientViewId, reportId, { status: nextStatus });
      await load();
    } catch (err) {
      setError(err?.message || t(`${COPY}.updateError`));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="casamapa-admin-page cm-data-reports">
      <header className="casamapa-admin-page__header">
        <div>
          <p className="casamapa-admin-page__eyebrow">{t(`${COPY}.eyebrow`)}</p>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="muted">{t(`${COPY}.subtitle`)}</p>
        </div>
        <button type="button" className="partner-dashboard-btn" onClick={load} disabled={loading}>
          {t("realEstate.casaMapa.admin.refresh")}
        </button>
      </header>

      <section className="cm-data-reports__summary">
        <article className="panel-card">
          <span className="muted">{t(`${COPY}.openCount`)}</span>
          <strong>{openCount}</strong>
        </article>
        <article className="panel-card">
          <span className="muted">{t(`${COPY}.showing`)}</span>
          <strong>
            {reports.length}/{total}
          </strong>
        </article>
      </section>

      <section className="panel-card casamapa-admin-section">
        <div className="cm-data-reports__filters">
          <label>
            <span>{t(`${COPY}.statusFilter`)}</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {STATUS_FILTERS.map((value) => (
                <option key={value || "all"} value={value}>
                  {value ? t(`${COPY}.statuses.${value}`) : t(`${COPY}.allStatuses`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t(`${COPY}.reasonFilter`)}</span>
            <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)}>
              <option value="">{t(`${COPY}.allReasons`)}</option>
              {reasons.map((reason) => (
                <option key={reason} value={reason}>
                  {t(`realEstate.casaMapa.reportData.reasons.${reason}`, { defaultValue: reason })}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? <p className="cm-data-reports__error">{error}</p> : null}
        {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}
        {!loading && !reports.length ? <p className="muted">{t(`${COPY}.empty`)}</p> : null}

        {reports.length ? (
          <div className="cm-data-reports__table-wrap">
            <table className="cm-data-reports__table">
              <thead>
                <tr>
                  <th>{t(`${COPY}.columns.listing`)}</th>
                  <th>{t(`${COPY}.columns.reason`)}</th>
                  <th>{t(`${COPY}.columns.reporter`)}</th>
                  <th>{t(`${COPY}.columns.status`)}</th>
                  <th>{t(`${COPY}.columns.reported`)}</th>
                  <th>{t(`${COPY}.columns.actions`)}</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.reportId}>
                    <td>
                      <div className="cm-data-reports__listing">
                        <strong>{report.listingTitle || t(`${COPY}.untitled`)}</strong>
                        <span className="muted">
                          {[report.listingNeighborhood, report.listingCity]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </span>
                        <Link
                          to={path(`listings/${report.canonicalListingId}`)}
                          className="cm-data-reports__link"
                        >
                          {t(`${COPY}.openListing`)}
                        </Link>
                      </div>
                    </td>
                    <td>
                      {t(`realEstate.casaMapa.reportData.reasons.${report.reasonCode}`, {
                        defaultValue: report.reasonCode,
                      })}
                    </td>
                    <td>{report.reporterEmail || "—"}</td>
                    <td>
                      <StatusBadge status={report.status} t={t} />
                    </td>
                    <td>{formatWhen(report.createdAt, locale)}</td>
                    <td>
                      <div className="cm-data-reports__actions">
                        {TRIAGE_ACTIONS.filter((action) => action !== report.status).map((action) => (
                          <button
                            key={action}
                            type="button"
                            className="partner-dashboard-btn partner-dashboard-btn--ghost"
                            disabled={busyId === report.reportId}
                            onClick={() => triage(report.reportId, action)}
                          >
                            {t(`${COPY}.actions.${action}`)}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}
